"use client"

import * as React from "react"
import { OptimizedImage } from "@/components/ui/OptimizedImage"
import useEmblaCarousel from "@/components/ui/use-embla-carousel"
import AutoScroll from "embla-carousel-auto-scroll"
import { cn } from "@/lib/utils"
import type { ApplicationsData } from "@/lib/content-parser"


/**
 * Applications Carousel Section
 *
 * 使用 Embla Carousel 实现无限循环轮播
 * 卡片有不同的高度和Y偏移，形成波浪效果
 */

// Design constants
const DESIGN_WIDTH = 1920
const DESIGN_HEIGHT = 1050  // 增加高度，留出更多上下空间
const CARD_WIDTH = 420
const CARD_GAP = 12
const TOP_PADDING = 60     // 顶部间距
const BOTTOM_PADDING = 120 // 底部间距（给按钮和进度条留空间）

// 卡片样式配置：每个位置的 Y 偏移和高度（循环模式 0-2）
const CARD_STYLES = [
  { yOffset: 0, height: 562 },    // Style 0
  { yOffset: 70, height: 562 },   // Style 1 - 下移 70px
  { yOffset: 140, height: 562 },  // Style 2 - 下移 140px
]

interface ApplicationsProps {
  data: ApplicationsData
  className?: string
}

export function Applications({ data, className }: ApplicationsProps) {
  
  if (!data) return null

  const images = data.images || []
  const totalImages = images.length

  // ===== DESKTOP EMBLA CAROUSEL (Infinite Loop + AutoScroll) =====
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      skipSnaps: false,
      dragFree: true,
      containScroll: false,
    },
    [
      AutoScroll({
        speed: 1,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
        stopOnFocusIn: true,
      }),
    ]
  )

  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const [scrollProgress, setScrollProgress] = React.useState(0)
  const lastActionTimeRef = React.useRef(0)
  const ACTION_THROTTLE = 300

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  const onScroll = React.useCallback(() => {
    if (!emblaApi) return
    const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()))
    setScrollProgress(progress)
  }, [emblaApi])

  React.useEffect(() => {
    if (!emblaApi) return
    onSelect()
    onScroll()
    emblaApi.on("select", onSelect)
    emblaApi.on("scroll", onScroll)
    emblaApi.on("reInit", onSelect)
    emblaApi.on("reInit", onScroll)
    return () => {
      emblaApi.off("select", onSelect)
      emblaApi.off("scroll", onScroll)
      emblaApi.off("reInit", onSelect)
      emblaApi.off("reInit", onScroll)
    }
  }, [emblaApi, onSelect, onScroll])

  const autoScrollPlugin = React.useMemo(() => {
    return emblaApi?.plugins()?.autoScroll
  }, [emblaApi])

  const goToPrev = React.useCallback(() => {
    if (!emblaApi) return
    const now = Date.now()
    if (now - lastActionTimeRef.current < ACTION_THROTTLE) return
    lastActionTimeRef.current = now

    autoScrollPlugin?.stop()
    emblaApi.scrollPrev()
    setTimeout(() => autoScrollPlugin?.play(), 2000)
  }, [emblaApi, autoScrollPlugin])

  const goToNext = React.useCallback(() => {
    if (!emblaApi) return
    const now = Date.now()
    if (now - lastActionTimeRef.current < ACTION_THROTTLE) return
    lastActionTimeRef.current = now

    autoScrollPlugin?.stop()
    emblaApi.scrollNext()
    setTimeout(() => autoScrollPlugin?.play(), 2000)
  }, [emblaApi, autoScrollPlugin])

  const getCardStyle = (index: number) => {
    return CARD_STYLES[index % CARD_STYLES.length]
  }

  const progressPercent = totalImages > 0
    ? ((selectedIndex + 1) / totalImages) * 100
    : 0

  // ===== MOBILE EMBLA CAROUSEL (Large Impact Cards + Loop) =====
  const [mobileEmblaRef, mobileEmblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    skipSnaps: false,
  })

  const [mobileSelectedIndex, setMobileSelectedIndex] = React.useState(0)

  React.useEffect(() => {
    if (!mobileEmblaApi) return
    const onMobileSelect = () => {
      setMobileSelectedIndex(mobileEmblaApi.selectedScrollSnap())
    }
    mobileEmblaApi.on("select", onMobileSelect)
    onMobileSelect()
    return () => {
      mobileEmblaApi.off("select", onMobileSelect)
    }
  }, [mobileEmblaApi])

  const goToMobilePrev = React.useCallback(() => {
    if (!mobileEmblaApi) return
    mobileEmblaApi.scrollPrev()
  }, [mobileEmblaApi])

  const goToMobileNext = React.useCallback(() => {
    if (!mobileEmblaApi) return
    mobileEmblaApi.scrollNext()
  }, [mobileEmblaApi])

  if (totalImages === 0) return null

  const cardWidthVw = (CARD_WIDTH / DESIGN_WIDTH) * 100
  const cardGapVw = (CARD_GAP / DESIGN_WIDTH) * 100

  return (
    <div className={cn("w-full bg-[#F6F4ED]", className)}>
      {/* ===== DESKTOP LAYOUT (lg and above, 100% pristine absolute wave carousel) ===== */}
      <div className="hidden lg:block w-full">
        <section
          className="relative w-full overflow-hidden bg-[#F6F4ED]"
          style={{ aspectRatio: `${DESIGN_WIDTH} / ${DESIGN_HEIGHT}` }}
        >
          {/* Embla Viewport */}
          <div
            className="absolute overflow-hidden"
            ref={emblaRef}
            style={{
              left: 0,
              right: 0,
              top: `${(TOP_PADDING / DESIGN_WIDTH) * 100}vw`,
              height: `${((562 + 140 + 30) / DESIGN_WIDTH) * 100}vw`,
            }}
          >
            {/* Embla Container */}
            <div
              className="flex h-full"
              style={{
                gap: `${cardGapVw}vw`,
                marginLeft: `${(-200 / DESIGN_WIDTH) * 100}vw`,
              }}
            >
              {images.map((imageUrl, index) => {
                const style = getCardStyle(index)
                const isLast = index === images.length - 1

                return (
                  <div
                    key={`desktop-slide-${index}`}
                    className="relative flex-shrink-0 overflow-hidden bg-[#D9D9D9] group transition-shadow duration-300 hover:shadow-lg hover:shadow-black/40"
                    style={{
                      width: `${cardWidthVw}vw`,
                      height: `${(style.height / DESIGN_WIDTH) * 100}vw`,
                      marginTop: `${(style.yOffset / DESIGN_WIDTH) * 100}vw`,
                      marginRight: isLast ? `${cardGapVw}vw` : undefined,
                      borderRadius: `${(30 / DESIGN_WIDTH) * 100}vw`,
                    }}
                  >
                    <OptimizedImage
                      image={imageUrl}
                      alt=""
                      size="large"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                  </div>
                )
              })}
            </div>
          </div>

          {/* Left Navigation Button */}
          <button
            className="absolute cursor-pointer group z-10"
            style={{
              left: `${(508 / DESIGN_WIDTH) * 100}%`,
              bottom: `${(BOTTOM_PADDING / DESIGN_WIDTH) * 100}vw`,
              width: `${(83 / DESIGN_WIDTH) * 100}vw`,
              height: `${(82 / DESIGN_WIDTH) * 100}vw`,
            }}
            onClick={goToPrev}
            aria-label="Previous slide"
          >
            <svg
              className="absolute inset-0 w-full h-full transition-opacity duration-300 group-hover:opacity-0"
              viewBox="0 0 83 82"
              fill="none"
            >
              <ellipse cx="41.5" cy="41" rx="40" ry="40" stroke="#464010" strokeWidth="2" fill="none"/>
              <path d="M50.9281 27.5226L48.6081 25.1592L32.4131 41.057L48.608 56.9547L50.9281 54.5914L37.3117 41.057L50.9281 27.5226Z" fill="#464010"/>
            </svg>
            <svg
              className="absolute inset-0 w-full h-full transition-opacity duration-300 opacity-0 group-hover:opacity-100"
              viewBox="0 0 83 82"
              fill="none"
            >
              <ellipse cx="41.5" cy="41" rx="41.5" ry="41" fill="#756F3F"/>
              <path d="M50.9281 27.5226L48.6081 25.1592L32.4131 41.057L48.608 56.9547L50.9281 54.5914L37.3117 41.057L50.9281 27.5226Z" fill="white"/>
            </svg>
          </button>

          {/* Right Navigation Button */}
          <button
            className="absolute cursor-pointer group z-10"
            style={{
              left: `${(1338 / DESIGN_WIDTH) * 100}%`,
              bottom: `${(BOTTOM_PADDING / DESIGN_WIDTH) * 100}vw`,
              width: `${(83 / DESIGN_WIDTH) * 100}vw`,
              height: `${(82 / DESIGN_WIDTH) * 100}vw`,
            }}
            onClick={goToNext}
            aria-label="Next slide"
          >
            <svg
              className="absolute inset-0 w-full h-full transition-opacity duration-300 group-hover:opacity-0"
              viewBox="0 0 83 82"
              fill="none"
            >
              <ellipse cx="41.5" cy="41" rx="40" ry="40" stroke="#464010" strokeWidth="2" fill="none"/>
              <path d="M32.0719 27.5226L34.3919 25.1592L50.5869 41.057L34.392 56.9547L32.0719 54.5914L45.6883 41.057L32.0719 27.5226Z" fill="#464010"/>
            </svg>
            <svg
              className="absolute inset-0 w-full h-full transition-opacity duration-300 opacity-0 group-hover:opacity-100"
              viewBox="0 0 83 82"
              fill="none"
            >
              <ellipse cx="41.5" cy="41" rx="41.5" ry="41" fill="#756F3F"/>
              <path d="M32.0719 27.5226L34.3919 25.1592L50.5869 41.057L34.392 56.9547L32.0719 54.5914L45.6883 41.057L32.0719 27.5226Z" fill="white"/>
            </svg>
          </button>

          {/* Progress Bar */}
          <div
            className="absolute rounded-full overflow-hidden"
            style={{
              left: `${(630 / DESIGN_WIDTH) * 100}%`,
              bottom: `${((BOTTOM_PADDING + 40) / DESIGN_WIDTH) * 100}vw`,
              width: `${(669 / DESIGN_WIDTH) * 100}vw`,
              height: `${(6 / DESIGN_WIDTH) * 100}vw`,
              backgroundColor: 'rgba(209, 209, 209, 0.52)',
            }}
          >
            <div
              className="h-full rounded-full transition-all duration-300 ease-out"
              style={{
                width: `${progressPercent}%`,
                backgroundColor: '#3F3F3F',
              }}
            />
          </div>
        </section>
      </div>

      {/* ===== MOBILE LAYOUT (below lg, premium large-image gallery, pure image display matching desktop) ===== */}
      <div className="block lg:hidden w-full py-12 px-4 bg-[#F6F4ED] overflow-hidden">
        {/* Embla Mobile Viewport */}
        <div className="overflow-hidden w-full max-w-md mx-auto" ref={mobileEmblaRef}>
          <div className="flex gap-4 touch-pan-y">
            {images.map((imageUrl, index) => (
              <div
                key={`mobile-slide-${index}`}
                className="relative flex-shrink-0 w-[85%] aspect-[4/5] rounded-3xl overflow-hidden bg-[#D9D9D9] border-4 border-white"
              >
                <OptimizedImage
                  image={imageUrl}
                  alt=""
                  size="large"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Navigation Controls & Pagination */}
        <div className="flex items-center justify-center gap-6 mt-8 max-w-xs mx-auto">
          <button
            className="w-12 h-12 rounded-full bg-[#BAB489] text-white flex items-center justify-center hover:bg-[#756F3F] active:scale-95 transition-all shadow-md flex-shrink-0"
            onClick={goToMobilePrev}
            aria-label="Previous image"
          >
            <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6"/>
            </svg>
          </button>

          {/* Pagination Dots */}
          <div className="flex items-center gap-2 flex-1 justify-center">
            {images.map((_, index) => (
              <button
                key={`dot-${index}`}
                className={cn(
                  "h-2.5 rounded-full transition-all duration-300",
                  mobileSelectedIndex === index ? "w-8 bg-[#756F3F]" : "w-2.5 bg-[#BAB489]/50"
                )}
                onClick={() => mobileEmblaApi?.scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          <button
            className="w-12 h-12 rounded-full bg-[#BAB489] text-white flex items-center justify-center hover:bg-[#756F3F] active:scale-95 transition-all shadow-md flex-shrink-0"
            onClick={goToMobileNext}
            aria-label="Next image"
          >
            <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="m9 18 6-6-6-6"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

export default Applications
